import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { formatNumberToUSD, calculateTermPmt } from 'src/shared/calculators';
import { showErrorStatus } from 'src/security/state/user-slice';
import { parseAmount, formatCurrencyInput, stripCurrencyInput } from '../hub-utils';
import type { PaymentProgramViewContext } from './use-payment-program-view';
import {
  getSavedInstallmentProgramPrefillFromEncounter,
  type CustomResultsNavState,
} from './saved-program-prefill';

/* UX and program calculation rules (this hook + custom-results / HRE views must honor them):
    - Program balance: Uses total adjusted PFR (`adjustedPfrAmt ?? encounter pfrAmt`), not remaining balance after
      paid-to-date. After the down payment, the remainder is paid in installments. If the patient entered an
      affordable recurring amount, use that amount for each full period; the final installment is exactly whatever
      balance remains (no separate “tolerance” band). If only down payment is collected, recurring is at most the
      optimal recurring and may be lower so uniform installments divide the remainder exactly.
    - Term length (partial last installment): n = ceil(balance ÷ recurring) payment periods. Custom-results / custom
      preauth: smallest snapped contract term (1 or multiples of 3 months; weekly uses the 12-week ladder) with enough
      periods. HRE (`hre-approval`): any positive integer — term is n months if billing is monthly, else ceil(n ÷ 4)
      months so term×4 weekly periods cover n.
    - Affordable down payment: Use the full amount entered (capped at PFR only) — do not reduce below what the user
      entered for the calculated program row (“maximized” in the sense of taking their full stated affordable down).
    - Minimum preauthorized recurring: From the rule when the highest preauthorized term is applied (`minRecurringPreapproved`).
    - Minimum preauthorized down: From the rule for the PFR (`minDownPaymentPreapproved`).
    - Down payment only: Recurring ≤ optimal; may be lower to balance; term from rule/snaps as above; affordable down as entered.
    - Recurring only: Down = optimal; affordable recurring as entered; term as above.
    - Both: Affordable down and recurring as entered; term as above.
    - Do not overwrite affordable field text when calculating the program or toggling program options.
    - Results table always reflects the calculated program from these rules; high risk also shows minimal preauthorized row.
    - Green = preauthorized thresholds met; red = high risk; min-down / min-recurring prompts when under rule floors.
    - Displayed down and recurring for the program never exceed PFR.
    - High risk + requested program → Process HRE; preauthorized path → Add Payment Details (see custom-results-view).
*/

/** Number of payment periods for a contract term expressed in months (monthly = term; weekly = term×4). */
function paymentPeriodsForTermMonths(termMonths: number, billingFrequency: 'monthly' | 'weekly'): number {
  if (termMonths <= 0) return 0;
  return billingFrequency === 'weekly' ? termMonths * 4 : termMonths;
}

/**
 * Number of payment periods required at `recurring` so the last installment is the remainder
 * (n−1 full payments of `recurring`, then balance − (n−1)×recurring ∈ [0, recurring]).
 */
function minPaymentPeriodsForPartialLast(balance: number, recurring: number): number {
  if (balance <= 0 || recurring <= 0) return 0;
  return Math.ceil(balance / recurring);
}

/**
 * Contract term (months) with partial last payment: n = ceil(balance ÷ recurring) payment periods;
 * monthly term = n months; weekly billing uses ceil(n ÷ 4) months so term×4 weekly periods cover n.
 */
function termFromBalanceAndRecurring(
  balance: number,
  recurring: number,
  billingFrequency: 'monthly' | 'weekly'
): number {
  if (balance <= 0 || recurring <= 0) return 0;
  const nNeed = minPaymentPeriodsForPartialLast(balance, recurring);
  if (nNeed <= 0) return 0;
  return billingFrequency === 'weekly' ? Math.ceil(nNeed / 4) : nNeed;
}

function snapTermToAllowed(termInMonths: number): number {
  if (termInMonths <= 0) return 0;
  if (termInMonths <= 1.5) return 1;
  return Math.round(termInMonths / 3) * 3;
}

/** Smallest allowed term (0, 1, or multiple of 3) that is >= x. */
function snapTermUpToAllowed(x: number): number {
  if (x <= 0) return 0;
  if (x <= 1) return 1;
  return Math.ceil(x / 3) * 3;
}

/** Snap term in weeks to allowed values: 0, 1, 2, 3, or a multiple of 12. */
function snapWeeksToAllowed(weeks: number): number {
  if (weeks <= 0) return 0;
  if (weeks <= 1.5) return 1;
  if (weeks <= 2.5) return 2;
  if (weeks <= 7.5) return 3;
  return Math.round(weeks / 12) * 12;
}

/** Smallest allowed weeks (0, 1, 2, 3, or multiple of 12) that is >= x. */
function snapWeeksUpToAllowed(x: number): number {
  if (x <= 0) return 0;
  if (x <= 1) return 1;
  if (x <= 2) return 2;
  if (x <= 3) return 3;
  return Math.ceil(x / 12) * 12;
}

/** Largest allowed term (months: 0, 1, or multiple of 3) that is <= x. */
function snapTermDownToAllowed(x: number): number {
  if (x <= 0) return 0;
  if (x < 1.5) return 1;
  return Math.floor(x / 3) * 3;
}

/** Largest allowed weeks (0, 1, 2, 3, or multiple of 12) that is <= x. */
function snapWeeksDownToAllowed(x: number): number {
  if (x <= 0) return 0;
  if (x < 1.5) return 1;
  if (x < 2.5) return 2;
  if (x < 7.5) return 3;
  return Math.floor(x / 12) * 12;
}

/**
 * Context slice that the custom hook adds. Base hook must provide the rest of PaymentProgramViewContext.
 */
export interface CustomProgramContextAddition {
  customDownPayment: string;
  setCustomDownPayment: (v: string) => void;
  customTerm: string;
  setCustomTerm: (v: string) => void;
  customRecurringAmount: string;
  setCustomRecurringAmount: (v: string) => void;
  downPaymentError: string;
  recurringAmountError: string;
  isHighRisk: boolean;
  /** Min preauthorized down from the matching rule (`minDownPmt` / % of PFR). */
  minDownPaymentPreapproved: number | null;
  /** Min preauthorized recurring when highest preauthorized term is applied. */
  minRecurringPreapproved: number | null;
  /** Min recurring at max term: affordable DP when DP field shown, else rule min down (prompt + preauth / high-risk checks). */
  minRecurringPreapprovedForPrompt: number | null;
  /** Highest preauthorized term (months). */
  minTermPreapproved: number | null;
  customDownPmt: number;
  customTermValue: number;
  customRecurringAmtValue: number;
  calculatedTerm: number;
  calculatedTermDisplay: number;
  customRecurringAmtFinal: number;
  handleDownPaymentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRecurringAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDownPaymentBlur: () => void;
  handleRecurringAmountBlur: () => void;
  handleSelectCustomProgram: () => void;
  handleSelectMinPreapprovedProgram: () => void;
  handleProcessHre: () => void;
  /** True when calculated program satisfies preauth mins (uses affordable-down-based recurring floor when applicable). */
  meetsPreauthMinimums: boolean;
}

/**
 * Custom program–only state, derived values, and handlers.
 * Call with the result of usePaymentProgramView (base) and merge: { ...base, ...useCustomProgramView(base) }.
 */
export function useCustomProgramView(
  base: Omit<PaymentProgramViewContext, keyof CustomProgramContextAddition>
): CustomProgramContextAddition {
  const location = useLocation();
  const {
    pfrAmt: encounterPfrAmt,
    adjustedPfrAmt,
    matchingRule,
    optDownPayment,
    recurringAmt,
    optimalTermNum,
    billingFrequency,
    showDownPayment,
    setShowDownPayment,
    showRecurringAmount,
    setShowRecurringAmount,
    setShowFields,
    createProgramAndNavigate,
    navigate,
    getProgramPath,
    programType,
    patientId,
    encounterId,
    isCustomResults,
    dispatch,
    hreApprovalStatus,
    setBillingFrequency,
    activeEncounter,
  } = base;

  /** Total adjusted PFR for custom/HRE math (not net of paid-to-date); aligns optimal % with full estimate. */
  const pfrAmt = adjustedPfrAmt ?? encounterPfrAmt;

  // Min preapproved amounts (used when highest preauthorized term is applied)
  const { minDownPaymentPreapproved, minRecurringPreapproved, minTermPreapproved } = useMemo(() => {
    const minDown: number | null =
      matchingRule && (matchingRule.minDownPmt != null || matchingRule.minDownPmt === 0)
        ? matchingRule.minDownPmtType === '%'
          ? ((matchingRule.minDownPmt || 0) * pfrAmt) / 100
          : matchingRule.minDownPmt ?? 0
        : null;
    const availableTermsArr =
      matchingRule?.availableTerms?.length > 0
        ? matchingRule.availableTerms.replace(' ', '').split(',').map((t: string) => +t).filter((n: number) => !isNaN(n) && n > 0)
        : [];
    const largestTerm = availableTermsArr.length > 0 ? Math.max(...availableTermsArr) : null;
    const minRecurring: number | null =
      minDown != null && largestTerm != null && largestTerm > 0
        ? billingFrequency === 'weekly'
          ? (pfrAmt - minDown) / (largestTerm * 4)
          : calculateTermPmt(pfrAmt, minDown, largestTerm)
        : null;
    const minTerm: number | null = largestTerm;
    return {
      minDownPaymentPreapproved: minDown,
      minRecurringPreapproved: minRecurring,
      minTermPreapproved: minTerm,
    };
  }, [matchingRule, pfrAmt, billingFrequency]);

  const [customDownPayment, setCustomDownPayment] = useState<string>('');
  const [customTerm, setCustomTerm] = useState<string>('');
  const [customRecurringAmount, setCustomRecurringAmount] = useState<string>('');
  const [downPaymentError, setDownPaymentError] = useState<string>('');
  const [recurringAmountError, setRecurringAmountError] = useState<string>('');
  const minRecurringPreapprovedForPrompt = useMemo(() => {
    const largestTerm = minTermPreapproved;
    if (largestTerm == null || largestTerm <= 0 || pfrAmt <= 0) return null;

    let effectiveDown: number;
    if (showDownPayment) {
      if (customDownPayment) {
        const parsed = parseAmount(customDownPayment);
        effectiveDown = !isNaN(parsed) ? parsed : optDownPayment || 0;
      } else {
        effectiveDown = optDownPayment || 0;
      }
    } else {
      if (minDownPaymentPreapproved == null) return null;
      effectiveDown = minDownPaymentPreapproved;
    }
    effectiveDown = Math.min(Math.max(0, effectiveDown), pfrAmt);
    const remainder = pfrAmt - effectiveDown;
    if (remainder <= 0) return null;

    return billingFrequency === 'weekly'
      ? remainder / (largestTerm * 4)
      : calculateTermPmt(pfrAmt, effectiveDown, largestTerm);
  }, [
    minTermPreapproved,
    minDownPaymentPreapproved,
    pfrAmt,
    billingFrequency,
    showDownPayment,
    customDownPayment,
    optDownPayment,
  ]);

  /** Custom-results / HRE: keep stored term aligned whenever affordable down or recurring changes. */
  const shouldSyncTermFromAmounts =
    programType === 'hre-approval' || isCustomResults;

  const defaultsSetForRouteRef = useRef<string>('');
  const prevBillingFrequencyRef = useRef<'monthly' | 'weekly'>(billingFrequency);
  /** Encounter key we already populated HRE amendment fields from (when landing without state) */
  const hrePopulatedFromEncounterKeyRef = useRef<string>('');
  /** After prefill from saved encounter program on custom-results (avoid repeating on re-renders). */
  const customResultsSavedProgramPrefillKeyRef = useRef<string>('');

  useEffect(() => {
    if (!isCustomResults) customResultsSavedProgramPrefillKeyRef.current = '';
  }, [isCustomResults]);

  // Set defaults for custom route (first step: programType === 'custom' && !isCustomResults)
  useEffect(() => {
    const currentRoute = `${programType}-${isCustomResults}`;
    if (defaultsSetForRouteRef.current === currentRoute) return;
    if (programType === 'custom' && !isCustomResults) {
      setShowFields(false);
      const currentOptDownPayment =
        matchingRule?.optimalDownPmtType === '%'
          ? ((matchingRule.optimalDownPmt || 0) * pfrAmt) / 100
          : matchingRule?.optimalDownPmt || 0;
      if (currentOptDownPayment > 0) setCustomDownPayment(formatCurrencyInput(currentOptDownPayment));
      if (matchingRule?.optimalTerm) setCustomTerm(matchingRule.optimalTerm.toString());
      const optTermNum = matchingRule?.optimalTerm ? parseInt(matchingRule.optimalTerm, 10) : 0;
      const optTermDivisor = billingFrequency === 'weekly' ? optTermNum * 4 : optTermNum;
      const optimalRecurringAmt =
        optTermDivisor > 0 && pfrAmt > currentOptDownPayment
          ? (pfrAmt - currentOptDownPayment) / optTermDivisor
          : 0;
      if (optimalRecurringAmt > 0) {
        setCustomRecurringAmount(formatCurrencyInput(optimalRecurringAmt));
        prevBillingFrequencyRef.current = billingFrequency;
      }
      defaultsSetForRouteRef.current = currentRoute;
    } else if (isCustomResults) {
      setShowFields(true);
      defaultsSetForRouteRef.current = currentRoute;
    }
  }, [programType, isCustomResults, matchingRule, pfrAmt, billingFrequency, setShowFields]);

  // When billing frequency changes on custom flow, convert recurring amount
  useEffect(() => {
    if (prevBillingFrequencyRef.current === billingFrequency) return;
    const prev = prevBillingFrequencyRef.current;
    prevBillingFrequencyRef.current = billingFrequency;
    const isCustomFlow = programType === 'custom' || isCustomResults;
    if (!isCustomFlow || !customRecurringAmount) return;
    const val = parseAmount(customRecurringAmount);
    if (isNaN(val) || val <= 0) return;
    if (prev === 'monthly' && billingFrequency === 'weekly') {
      setCustomRecurringAmount(formatCurrencyInput(val / 4));
    } else if (prev === 'weekly' && billingFrequency === 'monthly') {
      setCustomRecurringAmount(formatCurrencyInput(val * 4));
    }
  }, [billingFrequency, programType, isCustomResults, customRecurringAmount]);

  // Custom-results: apply checkbox state from navigation and prime amounts once per visit.
  // Do NOT list matchingRule / pfrAmt / billingFrequency in deps: when the rule loads after mount this
  // effect would run again and reset customTerm + affordable fields, so the results term never tracks edits.
  useEffect(() => {
    if (!isCustomResults || patientId <= 0 || encounterId <= 0) return;
    const navState = location.state as CustomResultsNavState | null;
    const fromCustom = navState && (navState.showDownPayment === true || navState.showRecurringAmount === true);
    if (fromCustom) {
      if (navState.showDownPayment) setShowDownPayment(true);
      if (navState.showRecurringAmount) setShowRecurringAmount(true);
      setShowFields(true);

      const encounterKey = `${patientId}-${encounterId}`;

      if (navState.prefillFromSavedProgram) {
        const bf = navState.savedBillingFrequency === 'weekly' ? 'weekly' : 'monthly';
        if (bf !== billingFrequency) {
          setBillingFrequency(bf);
          prevBillingFrequencyRef.current = bf;
        }
        const d = navState.savedDownPmtAmt ?? 0;
        const r = navState.savedPaymentDueAmt ?? 0;
        if (navState.showDownPayment && d > 0) {
          setCustomDownPayment(formatCurrencyInput(d));
        }
        if (navState.showRecurringAmount && r > 0) {
          setCustomRecurringAmount(formatCurrencyInput(r));
        }
        if (navState.savedTermsInMonths != null && navState.savedTermsInMonths > 0) {
          setCustomTerm(String(navState.savedTermsInMonths));
        }
        customResultsSavedProgramPrefillKeyRef.current = encounterKey;
        return;
      }

      const currentOptDownPayment =
        matchingRule?.optimalDownPmtType === '%'
          ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt) / 100
          : matchingRule?.optimalDownPmt || 0;
      if (currentOptDownPayment > 0) setCustomDownPayment(formatCurrencyInput(currentOptDownPayment));
      if (matchingRule?.optimalTerm) setCustomTerm(matchingRule.optimalTerm.toString());
      const optTermNum = matchingRule?.optimalTerm ? parseInt(matchingRule.optimalTerm, 10) : 0;
      const optTermDivisor = billingFrequency === 'weekly' ? optTermNum * 4 : optTermNum;
      const optimalRecurringAmt =
        optTermDivisor > 0 && pfrAmt > currentOptDownPayment
          ? (pfrAmt - currentOptDownPayment) / optTermDivisor
          : 0;
      if (optimalRecurringAmt > 0) setCustomRecurringAmount(formatCurrencyInput(optimalRecurringAmt));
      return;
    }
    navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom`), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit matchingRule/pfrAmt/billingFrequency (see comment above this effect)
  }, [isCustomResults, patientId, encounterId, location.state, navigate, getProgramPath, setShowDownPayment, setShowRecurringAmount, setShowFields]);

  // Custom-results: if encounter already has a saved installment program, prefill affordable down / recurring (overrides optimal priming when present).
  useEffect(() => {
    if (!isCustomResults || patientId <= 0 || encounterId <= 0) return;
    const navState = location.state as CustomResultsNavState | null;
    if (navState?.prefillFromSavedProgram) return;

    const fromCustom =
      navState && (navState.showDownPayment === true || navState.showRecurringAmount === true);
    if (!fromCustom) return;

    const encounterKey = `${patientId}-${encounterId}`;
    if (customResultsSavedProgramPrefillKeyRef.current === encounterKey) return;

    const saved = getSavedInstallmentProgramPrefillFromEncounter(activeEncounter);
    if (!saved) return;

    customResultsSavedProgramPrefillKeyRef.current = encounterKey;
    setShowFields(true);
    if (saved.downPmtAmt > 0) {
      setShowDownPayment(true);
      setCustomDownPayment(formatCurrencyInput(saved.downPmtAmt));
    }
    if (saved.paymentDueAmt > 0) {
      setShowRecurringAmount(true);
      setCustomRecurringAmount(formatCurrencyInput(saved.paymentDueAmt));
    }
    if (saved.billingFrequency !== billingFrequency) {
      setBillingFrequency(saved.billingFrequency);
      prevBillingFrequencyRef.current = saved.billingFrequency;
    }
    if (saved.termsInMonths > 0) {
      setCustomTerm(String(saved.termsInMonths));
    }
  }, [
    isCustomResults,
    patientId,
    encounterId,
    activeEncounter,
    location.state,
    billingFrequency,
    setShowDownPayment,
    setShowRecurringAmount,
    setShowFields,
    setBillingFrequency,
  ]);

  // When the rule or PFR arrives after mount: custom step 1 only — prefill empty hidden state with optimal.
  // Custom-results / HRE affordable fields: no effect-driven refill while empty; blur restores saved program or optimal.
  useEffect(() => {
    if (patientId <= 0 || encounterId <= 0) return;
    if (!matchingRule || pfrAmt <= 0) return;

    const currentOptDownPayment =
      matchingRule.optimalDownPmtType === '%'
        ? ((matchingRule.optimalDownPmt || 0) * pfrAmt) / 100
        : matchingRule.optimalDownPmt || 0;
    const optTermNum = matchingRule.optimalTerm ? parseInt(String(matchingRule.optimalTerm), 10) : 0;
    const optTermDivisor = billingFrequency === 'weekly' ? optTermNum * 4 : optTermNum;
    const optimalRecurringAmt =
      optTermDivisor > 0 && pfrAmt > currentOptDownPayment
        ? (pfrAmt - currentOptDownPayment) / optTermDivisor
        : 0;

    const onCustomIntro = programType === 'custom' && !isCustomResults;
    if (onCustomIntro) {
      if (customDownPayment.trim() === '' && currentOptDownPayment > 0) {
        setCustomDownPayment(formatCurrencyInput(currentOptDownPayment));
      }
      if (!customTerm.trim() && matchingRule.optimalTerm) {
        setCustomTerm(String(matchingRule.optimalTerm));
      }
      if (customRecurringAmount.trim() === '' && optimalRecurringAmt > 0) {
        setCustomRecurringAmount(formatCurrencyInput(optimalRecurringAmt));
        prevBillingFrequencyRef.current = billingFrequency;
      }
      return;
    }

    // custom-results / HRE: do not inject optimal into empty fields while typing — blur restores saved program or optimal.
  }, [
    programType,
    isCustomResults,
    patientId,
    encounterId,
    matchingRule,
    pfrAmt,
    billingFrequency,
    customDownPayment,
    customRecurringAmount,
    customTerm,
  ]);

  // HRE approval: restore custom program state from navigation (when we have hreProgramValues in state)
  useEffect(() => {
    if (programType !== 'hre-approval' || !location.state) return;
    const navState = location.state as { hreProgramValues?: { downPmtAmt: number; terms: number; recurringAmt: number; billingFrequency: 'monthly' | 'weekly' } } | null;
    const values = navState?.hreProgramValues;
    if (!values) return;
    setShowDownPayment(true);
    setShowRecurringAmount(true);
    setShowFields(true);
    setCustomDownPayment(formatCurrencyInput(values.downPmtAmt));
    setCustomTerm(values.terms.toString());
    setCustomRecurringAmount(formatCurrencyInput(values.recurringAmt));
    setBillingFrequency(values.billingFrequency);
    prevBillingFrequencyRef.current = values.billingFrequency;
    defaultsSetForRouteRef.current = `${programType}-${isCustomResults}`;
  }, [programType, isCustomResults, location.state, setShowDownPayment, setShowRecurringAmount, setShowFields, setBillingFrequency]);

  // Clear HRE defaults ref when leaving hre-approval so we can repopulate when returning
  useEffect(() => {
    if (programType !== 'hre-approval') {
      defaultsSetForRouteRef.current = '';
      hrePopulatedFromEncounterKeyRef.current = '';
    }
  }, [programType]);

  // HRE approval (custom view): when landing without state, populate amendment fields from get PE response (activeEncounter from base)
  useEffect(() => {
    if (programType !== 'hre-approval') return;
    const navState = location.state as { hreProgramValues?: unknown } | null;
    if (navState?.hreProgramValues) return; // already handled by navigation-state effect above
    const program = activeEncounter?.patientPaymentProgram?.[0];
    const schedule = program?.patientPaymentSchedule;
    if (!program || !schedule) return;
    const encounterKey = `${activeEncounter?.patientId ?? ''}-${activeEncounter?.patientEncounterId ?? ''}`;
    if (hrePopulatedFromEncounterKeyRef.current === encounterKey) return;
    hrePopulatedFromEncounterKeyRef.current = encounterKey;
    const downPmtAmt = program.downPmtAmt ?? schedule.downPmtAmt ?? 0;
    const scheduleAny = schedule as unknown as Record<string, unknown>;
    const rawPaymentDue = schedule.paymentDueAmt ?? scheduleAny?.paymentDueAmt;
    const paymentDueAmt =
      typeof rawPaymentDue === 'number' && !Number.isNaN(rawPaymentDue)
        ? rawPaymentDue
        : Number(rawPaymentDue) || 0;
    const termsRaw = schedule.terms ?? (scheduleAny?.terms as number) ?? 0;
    const isWeekly = schedule.paymentFreq === 'W' || schedule.paymentFreq === 'weekly';
    const termsInMonths = isWeekly && termsRaw > 0 ? termsRaw / 4 : termsRaw;
    const freq: 'monthly' | 'weekly' = isWeekly ? 'weekly' : 'monthly';
    setShowDownPayment(true);
    setShowRecurringAmount(true);
    setShowFields(true);
    if (downPmtAmt > 0) setCustomDownPayment(formatCurrencyInput(downPmtAmt));
    if (termsInMonths > 0) setCustomTerm(String(Math.round(termsInMonths)));
    setCustomRecurringAmount(formatCurrencyInput(paymentDueAmt));
    setBillingFrequency(freq);
    prevBillingFrequencyRef.current = freq;
  }, [programType, location.state, activeEncounter, setShowDownPayment, setShowRecurringAmount, setShowFields, setBillingFrequency]);

  const handleDownPaymentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = stripCurrencyInput(e.target.value);
      const value = parseAmount(raw);
      if (!isNaN(value) && value > pfrAmt) {
        setDownPaymentError(`Down payment cannot exceed ${formatNumberToUSD(pfrAmt)}`);
        setCustomDownPayment(formatCurrencyInput(pfrAmt));
      } else {
        setCustomDownPayment(raw);
        if (downPaymentError) setDownPaymentError('');
      }
    },
    [downPaymentError, pfrAmt]
  );

  const handleRecurringAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = stripCurrencyInput(e.target.value);
      setCustomRecurringAmount(raw);
      if (recurringAmountError) setRecurringAmountError('');
      const recurringAmtVal = parseAmount(raw);
      if (recurringAmtVal > 0 && pfrAmt > 0) {
        const currentOptDownPayment =
          matchingRule?.optimalDownPmtType === '%'
            ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt) / 100
            : matchingRule?.optimalDownPmt || 0;
        const downPmt =
          showDownPayment && customDownPayment ? parseAmount(customDownPayment) : currentOptDownPayment || 0;
        const remainingPfr = pfrAmt - (showDownPayment ? downPmt : currentOptDownPayment);
        if (showRecurringAmount && !showDownPayment && recurringAmtVal > remainingPfr) {
          setRecurringAmountError(
            `Recurring cannot exceed ${formatNumberToUSD(remainingPfr)} (remaining after down payment)`
          );
          setCustomRecurringAmount(formatCurrencyInput(remainingPfr));
        }
        const effectiveRecurring =
          showRecurringAmount && !showDownPayment && recurringAmtVal > remainingPfr
            ? remainingPfr
            : recurringAmtVal;
        if (pfrAmt > downPmt && effectiveRecurring > 0) {
          const remaining = pfrAmt - (showDownPayment ? downPmt : currentOptDownPayment);
          const termForStorage = termFromBalanceAndRecurring(
            remaining,
            effectiveRecurring,
            billingFrequency
          );
          if (termForStorage > 0) setCustomTerm(termForStorage.toString());
        }
      }
    },
    [
      recurringAmountError,
      pfrAmt,
      showDownPayment,
      showRecurringAmount,
      customDownPayment,
      matchingRule,
      billingFrequency,
    ]
  );

  const handleDownPaymentBlur = useCallback(() => {
    let finalValue = parseAmount(customDownPayment);
    if (!isNaN(finalValue) && finalValue > pfrAmt) {
      setDownPaymentError(`Down payment cannot exceed ${formatNumberToUSD(pfrAmt)}`);
      finalValue = pfrAmt;
      setCustomDownPayment(formatCurrencyInput(pfrAmt));
    } else {
      setDownPaymentError('');
      if (!isNaN(finalValue) && finalValue > 0) {
        setCustomDownPayment(formatCurrencyInput(finalValue));
      }
    }

    if (
      (isCustomResults || programType === 'hre-approval') &&
      showDownPayment &&
      customDownPayment.trim() === ''
    ) {
      const saved = getSavedInstallmentProgramPrefillFromEncounter(activeEncounter);
      const optDown =
        matchingRule?.optimalDownPmtType === '%'
          ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt) / 100
          : matchingRule?.optimalDownPmt || 0;
      if (saved && saved.downPmtAmt > 0) {
        setCustomDownPayment(formatCurrencyInput(saved.downPmtAmt));
        finalValue = saved.downPmtAmt;
      } else if (optDown > 0) {
        setCustomDownPayment(formatCurrencyInput(optDown));
        finalValue = optDown;
      }
    }

    if (showRecurringAmount && customRecurringAmount) {
      const recurringAmtVal = parseAmount(customRecurringAmount);
      if (recurringAmtVal > 0 && pfrAmt > finalValue) {
        const termForStorage = termFromBalanceAndRecurring(
          pfrAmt - finalValue,
          recurringAmtVal,
          billingFrequency
        );
        if (termForStorage > 0) setCustomTerm(termForStorage.toString());
      }
    } else if (showDownPayment && !showRecurringAmount && recurringAmt > 0 && pfrAmt > finalValue) {
      const termForStorage = termFromBalanceAndRecurring(
        pfrAmt - finalValue,
        recurringAmt,
        billingFrequency
      );
      if (termForStorage > 0) setCustomTerm(termForStorage.toString());
    }
  }, [
    customDownPayment,
    customRecurringAmount,
    showRecurringAmount,
    showDownPayment,
    pfrAmt,
    billingFrequency,
    recurringAmt,
    isCustomResults,
    programType,
    activeEncounter,
    matchingRule,
  ]);

  const handleRecurringAmountBlur = useCallback(() => {
    let finalValue = parseAmount(customRecurringAmount);
    const currentOptDownPayment =
      matchingRule?.optimalDownPmtType === '%'
        ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt) / 100
        : matchingRule?.optimalDownPmt || 0;
    const remainingPfr = pfrAmt - currentOptDownPayment;
    if (showRecurringAmount && !showDownPayment && !isNaN(finalValue) && finalValue > remainingPfr) {
      setRecurringAmountError(
        `Recurring cannot exceed ${formatNumberToUSD(remainingPfr)} (remaining after down payment)`
      );
      finalValue = remainingPfr;
      setCustomRecurringAmount(formatCurrencyInput(remainingPfr));
    } else if (!isNaN(finalValue) && finalValue > pfrAmt) {
      setRecurringAmountError(`Recurring amount cannot exceed ${formatNumberToUSD(pfrAmt)}`);
      setCustomRecurringAmount(formatCurrencyInput(pfrAmt));
      finalValue = pfrAmt;
    } else {
      setRecurringAmountError('');
      if (!isNaN(finalValue) && finalValue > 0) {
        setCustomRecurringAmount(formatCurrencyInput(finalValue));
      }
    }

    if (
      (isCustomResults || programType === 'hre-approval') &&
      showRecurringAmount &&
      customRecurringAmount.trim() === ''
    ) {
      const saved = getSavedInstallmentProgramPrefillFromEncounter(activeEncounter);
      const optTermNum = matchingRule?.optimalTerm ? parseInt(String(matchingRule.optimalTerm), 10) : 0;
      const optTermDivisor = billingFrequency === 'weekly' ? optTermNum * 4 : optTermNum;
      const optimalRecurringAmt =
        optTermDivisor > 0 && pfrAmt > currentOptDownPayment
          ? (pfrAmt - currentOptDownPayment) / optTermDivisor
          : 0;
      if (saved && saved.paymentDueAmt > 0) {
        setCustomRecurringAmount(formatCurrencyInput(saved.paymentDueAmt));
        finalValue = saved.paymentDueAmt;
      } else if (optimalRecurringAmt > 0) {
        setCustomRecurringAmount(formatCurrencyInput(optimalRecurringAmt));
        finalValue = optimalRecurringAmt;
      }
    }

    const downPmt =
      showDownPayment && customDownPayment ? parseAmount(customDownPayment) : currentOptDownPayment || 0;
    if (finalValue > 0 && pfrAmt > downPmt) {
      const termForStorage = termFromBalanceAndRecurring(
        pfrAmt - downPmt,
        finalValue,
        billingFrequency
      );
      if (termForStorage > 0) setCustomTerm(termForStorage.toString());
    }
  }, [
    customRecurringAmount,
    customDownPayment,
    showDownPayment,
    showRecurringAmount,
    matchingRule,
    pfrAmt,
    billingFrequency,
    isCustomResults,
    programType,
    activeEncounter,
  ]);

  useEffect(() => {
    if (!shouldSyncTermFromAmounts) return;

    const initialDownPmtSync =
      showDownPayment && customDownPayment ? parseAmount(customDownPayment) : optDownPayment || 0;
    const recurringVal = showRecurringAmount ? parseAmount(customRecurringAmount || '') : 0;

    if (showRecurringAmount && recurringVal > 0) {
      const balanceAfterDp = Math.max(0, pfrAmt - Math.min(initialDownPmtSync, pfrAmt));
      const t =
        balanceAfterDp <= 0
          ? 0
          : termFromBalanceAndRecurring(
              balanceAfterDp,
              recurringVal,
              billingFrequency
            );
      if (t > 0) {
        const s = String(t);
        if (customTerm !== s) setCustomTerm(s);
      }
    } else if (showDownPayment && !showRecurringAmount && recurringAmt > 0 && pfrAmt > initialDownPmtSync) {
      const t = termFromBalanceAndRecurring(
        pfrAmt - initialDownPmtSync,
        recurringAmt,
        billingFrequency
      );
      if (t > 0) {
        const s = String(t);
        if (customTerm !== s) setCustomTerm(s);
      }
    } else if (
      showDownPayment &&
      !showRecurringAmount &&
      recurringAmt <= 0 &&
      pfrAmt > initialDownPmtSync
    ) {
      const remaining = pfrAmt - initialDownPmtSync;
      const implied =
        optimalTermNum > 0 ? remaining / optimalTermNum : remaining;
      const t =
        implied > 0 && remaining > 0
          ? termFromBalanceAndRecurring(remaining, implied, billingFrequency)
          : 0;
      if (t > 0) {
        const s = String(t);
        if (customTerm !== s) setCustomTerm(s);
      }
    }
  }, [
    shouldSyncTermFromAmounts,
    showDownPayment,
    showRecurringAmount,
    customDownPayment,
    customRecurringAmount,
    pfrAmt,
    optDownPayment,
    recurringAmt,
    optimalTermNum,
    billingFrequency,
    customTerm,
  ]);

  const initialDownPmt =
    showDownPayment && customDownPayment ? parseAmount(customDownPayment) : optDownPayment || 0;
  const customTermValue = parseFloat(customTerm) || 0;
  const customRecurringAmtValue = showRecurringAmount ? parseAmount(customRecurringAmount || '') : 0;

  const balanceAfterDp = Math.max(0, pfrAmt - Math.min(initialDownPmt, pfrAmt));

  /** Parsed affordable recurring when that field is shown (avoids stale customTerm while typing). */
  const hasAffordableRecurring = showRecurringAmount && customRecurringAmtValue > 0;

  /** Custom-results or HRE: never show optimal `customTerm` as the program term — derive from amounts. */
  const onCustomAmountsStep = isCustomResults || programType === 'hre-approval';

  let calculatedTerm = 0;

  if (hasAffordableRecurring) {
    calculatedTerm =
      balanceAfterDp <= 0
        ? 0
        : termFromBalanceAndRecurring(
            balanceAfterDp,
            customRecurringAmtValue,
            billingFrequency
          );
  } else if (showRecurringAmount) {
    calculatedTerm = 0;
  } else if (onCustomAmountsStep && showDownPayment && !showRecurringAmount && pfrAmt > initialDownPmt) {
    const remaining = pfrAmt - initialDownPmt;
    const impliedRecurringPerPeriod =
      recurringAmt > 0
        ? recurringAmt
        : optimalTermNum > 0
          ? remaining / optimalTermNum
          : remaining;
    calculatedTerm =
      impliedRecurringPerPeriod > 0 && remaining > 0
        ? termFromBalanceAndRecurring(
            remaining,
            impliedRecurringPerPeriod,
            billingFrequency
          )
        : 0;
  } else {
    calculatedTerm = customTermValue;
    const dpOnlyFromRuleTerms =
      showDownPayment && !showRecurringAmount && recurringAmt > 0 && pfrAmt > initialDownPmt;
    if (dpOnlyFromRuleTerms) {
      const remaining = pfrAmt - initialDownPmt;
      calculatedTerm = termFromBalanceAndRecurring(remaining, recurringAmt, billingFrequency);
    } else if (calculatedTerm === 0 && customRecurringAmtValue > 0 && pfrAmt > initialDownPmt) {
      const termInPeriods = Math.ceil((pfrAmt - initialDownPmt) / customRecurringAmtValue);
      calculatedTerm =
        billingFrequency === 'weekly'
          ? snapWeeksUpToAllowed(termInPeriods) / 4
          : snapTermUpToAllowed(termInPeriods);
    }
    if (calculatedTerm > 0 && !dpOnlyFromRuleTerms) {
      calculatedTerm =
        billingFrequency === 'weekly'
          ? snapWeeksToAllowed(calculatedTerm * 4) / 4
          : snapTermToAllowed(calculatedTerm);
    }

    if (
      showDownPayment &&
      !showRecurringAmount &&
      recurringAmt > 0 &&
      pfrAmt > initialDownPmt &&
      calculatedTerm > 0
    ) {
      const minTermDivisor = (pfrAmt - initialDownPmt) / recurringAmt;
      const minTermSnapped =
        billingFrequency === 'weekly'
          ? snapWeeksUpToAllowed(minTermDivisor) / 4
          : snapTermUpToAllowed(Math.ceil(minTermDivisor));
      if (calculatedTerm < minTermSnapped) {
        calculatedTerm = minTermSnapped;
      }
    }

    const termInPeriodsForCap =
      billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm;
    const recurringForCap =
      showRecurringAmount && customRecurringAmount
        ? customRecurringAmtValue
        : showDownPayment && !showRecurringAmount
          ? recurringAmt
          : calculatedTerm > 0 && pfrAmt > initialDownPmt
            ? (pfrAmt - initialDownPmt) / (billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm)
            : 0;
    const isUserEnteredRecurringInner = hasAffordableRecurring;
    const isDownPaymentOnlyInner = showDownPayment && !showRecurringAmount;
    if (recurringForCap > 0 && !isUserEnteredRecurringInner) {
      const remainder = pfrAmt - initialDownPmt - termInPeriodsForCap * recurringForCap;
      if (remainder < 0 && !isDownPaymentOnlyInner) {
        const maxTermInPeriods = Math.floor((pfrAmt - initialDownPmt) / recurringForCap);
        if (billingFrequency === 'weekly') {
          calculatedTerm = snapWeeksDownToAllowed(maxTermInPeriods) / 4;
        } else {
          calculatedTerm = snapTermDownToAllowed(maxTermInPeriods);
        }
      }
    }
  }

  const termInPeriodsForCap =
    billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm;
  const recurringForCap =
    showRecurringAmount && customRecurringAmount
      ? customRecurringAmtValue
      : showDownPayment && !showRecurringAmount
        ? recurringAmt
        : calculatedTerm > 0 && pfrAmt > initialDownPmt
          ? (pfrAmt - initialDownPmt) / (billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm)
          : 0;
  const isUserEnteredRecurring = hasAffordableRecurring;
  const isDownPaymentOnly = showDownPayment && !showRecurringAmount;

  const calculatedTermDisplay =
    billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm;
  const calculatedTermDivisor = billingFrequency === 'weekly' ? calculatedTermDisplay : calculatedTerm;
  const customDownPmt = initialDownPmt;
  // When down payment only: recurring is at most optimal; use lower per-period amount when needed to balance.
  let customRecurringAmtFinal =
    showRecurringAmount && customRecurringAmount
      ? parseAmount(customRecurringAmount)
      : isDownPaymentOnly && calculatedTermDivisor > 0 && pfrAmt > customDownPmt
        ? Math.min(recurringAmt, (pfrAmt - customDownPmt) / calculatedTermDivisor)
        : isDownPaymentOnly
          ? recurringAmt
          : calculatedTermDivisor > 0 && pfrAmt > customDownPmt
            ? (pfrAmt - customDownPmt) / calculatedTermDivisor
            : 0;

  // Rule: calculated program down payment or recurring can never be greater than the PFR amount.
  const customDownPmtFinal = Math.min(customDownPmt, pfrAmt);
  const customRecurringAmtFinalClamped = Math.min(customRecurringAmtFinal, pfrAmt);

  const meetsPreauthMinimums = useMemo(() => {
    if (
      minDownPaymentPreapproved == null ||
      minRecurringPreapproved == null ||
      minTermPreapproved == null ||
      calculatedTerm <= 0
    ) {
      return false;
    }
    const minDown = minDownPaymentPreapproved;
    const maxTerm = minTermPreapproved;
    const minRec = minRecurringPreapprovedForPrompt ?? minRecurringPreapproved;
    const AMOUNT_TOLERANCE = 0.99;
    const dpAboveMinAndTermBelowMax =
      customDownPmtFinal > minDown && calculatedTerm < maxTerm;
    const recurringAboveMinAndTermBelowMax =
      customRecurringAmtFinalClamped > minRec && calculatedTerm < maxTerm;
    return (
      customDownPmtFinal >= minDown &&
      (dpAboveMinAndTermBelowMax ||
        recurringAboveMinAndTermBelowMax ||
        (calculatedTerm <= maxTerm &&
          customDownPmtFinal >= minDown - AMOUNT_TOLERANCE &&
          customRecurringAmtFinalClamped >= minRec - AMOUNT_TOLERANCE))
    );
  }, [
    minDownPaymentPreapproved,
    minRecurringPreapproved,
    minRecurringPreapprovedForPrompt,
    minTermPreapproved,
    customDownPmtFinal,
    customRecurringAmtFinalClamped,
    calculatedTerm,
  ]);

  const isHighRisk = useMemo(() => {
    if (calculatedTerm <= 0) return false;
    if (matchingRule && matchingRule.ruleId > 0 && matchingRule.ruleId !== -1) {
      return !meetsPreauthMinimums;
    }
    if (calculatedTerm > 0 && (!matchingRule || matchingRule.ruleId === 0 || matchingRule.ruleId === -1)) {
      return true;
    }
    return false;
  }, [calculatedTerm, matchingRule, meetsPreauthMinimums]);

  const handleSelectCustomProgram = useCallback(async () => {
    if (programType === 'hre-approval' && !hreApprovalStatus) {
      dispatch(showErrorStatus('Please select an outcome.'));
      return;
    }

    const calculatedCustomDownPmt = Math.min(
      showDownPayment && customDownPayment
        ? parseAmount(customDownPayment)
        : showRecurringAmount && !showDownPayment
          ? customDownPmtFinal
          : optDownPayment || 0,
      pfrAmt
    );
    const customRecurringAmtVal =
      showRecurringAmount && customRecurringAmount ? parseAmount(customRecurringAmount) : 0;
    let termForProgram: number;
    let recurringAmtForProgram: number;
    if (showDownPayment && !showRecurringAmount) {
      termForProgram = calculatedTerm;
      recurringAmtForProgram = customRecurringAmtFinalClamped;
    } else if (
      showRecurringAmount &&
      customRecurringAmount &&
      customRecurringAmtVal > 0 &&
      calculatedTerm > 0
    ) {
      termForProgram = calculatedTerm;
      recurringAmtForProgram = Math.min(customRecurringAmtVal, pfrAmt);
    } else {
      termForProgram = parseFloat(customTerm) || 0;
      if (termForProgram === 0 && customRecurringAmtVal > 0 && pfrAmt > calculatedCustomDownPmt) {
        const termInPeriods = Math.floor((pfrAmt - calculatedCustomDownPmt) / customRecurringAmtVal);
        termForProgram =
          billingFrequency === 'weekly' ? Math.ceil(termInPeriods / 4) : termInPeriods;
      }
      const effectiveTermForProgram =
        billingFrequency === 'weekly' ? termForProgram * 4 : termForProgram;
      recurringAmtForProgram = Math.min(
        effectiveTermForProgram > 0 && pfrAmt > calculatedCustomDownPmt
          ? (pfrAmt - calculatedCustomDownPmt) / effectiveTermForProgram
          : customRecurringAmtVal > 0
            ? customRecurringAmtVal
            : 0,
        pfrAmt
      );
    }
    const isReject =
      programType === 'hre-approval' &&
      (hreApprovalStatus === 'rejected-by-client' || hreApprovalStatus === 'rejected-by-guarantor');
    createProgramAndNavigate(
      {
        isPaidInFull: false,
        downPmtAmt: calculatedCustomDownPmt,
        isHighRisk,
        terms: termForProgram,
        recurringAmt: recurringAmtForProgram,
      },
      isReject ? { navigateTo: 'ioc' } : undefined
    );
  }, [
    programType,
    hreApprovalStatus,
    dispatch,
    showDownPayment,
    customDownPayment,
    showRecurringAmount,
    customRecurringAmount,
    customTerm,
    calculatedTerm,
    customRecurringAmtFinalClamped,
    customDownPmtFinal,
    optDownPayment,
    pfrAmt,
    isHighRisk,
    billingFrequency,
    createProgramAndNavigate,
  ]);

  const handleProcessHre = useCallback(() => {
    const calculatedCustomDownPmt = Math.min(
      showDownPayment && customDownPayment
        ? parseAmount(customDownPayment)
        : showRecurringAmount && !showDownPayment
          ? customDownPmtFinal
          : optDownPayment || 0,
      pfrAmt
    );
    const customRecurringAmtVal =
      showRecurringAmount && customRecurringAmount ? parseAmount(customRecurringAmount) : 0;
    let termForProgram: number;
    let recurringAmtForProgram: number;
    if (showDownPayment && !showRecurringAmount) {
      termForProgram = calculatedTerm;
      recurringAmtForProgram = customRecurringAmtFinalClamped;
    } else if (
      showRecurringAmount &&
      customRecurringAmount &&
      customRecurringAmtVal > 0 &&
      calculatedTerm > 0
    ) {
      termForProgram = calculatedTerm;
      recurringAmtForProgram = Math.min(customRecurringAmtVal, pfrAmt);
    } else {
      termForProgram = parseFloat(customTerm) || 0;
      if (termForProgram === 0 && customRecurringAmtVal > 0 && pfrAmt > calculatedCustomDownPmt) {
        const termInPeriods = Math.floor((pfrAmt - calculatedCustomDownPmt) / customRecurringAmtVal);
        termForProgram =
          billingFrequency === 'weekly' ? Math.ceil(termInPeriods / 4) : termInPeriods;
      }
      const effectiveTermForProgram =
        billingFrequency === 'weekly' ? termForProgram * 4 : termForProgram;
      recurringAmtForProgram = Math.min(
        effectiveTermForProgram > 0 && pfrAmt > calculatedCustomDownPmt
          ? (pfrAmt - calculatedCustomDownPmt) / effectiveTermForProgram
          : customRecurringAmtVal > 0
            ? customRecurringAmtVal
            : 0,
        pfrAmt
      );
    }
    createProgramAndNavigate(
      {
        isPaidInFull: false,
        downPmtAmt: calculatedCustomDownPmt,
        isHighRisk: true,
        terms: termForProgram,
        recurringAmt: recurringAmtForProgram,
      },
      { navigateTo: 'hre-approval' }
    );
  }, [
    showDownPayment,
    customDownPayment,
    showRecurringAmount,
    customRecurringAmount,
    customTerm,
    calculatedTerm,
    customRecurringAmtFinalClamped,
    customDownPmtFinal,
    optDownPayment,
    pfrAmt,
    billingFrequency,
    createProgramAndNavigate,
  ]);

  const handleSelectMinPreapprovedProgram = useCallback(() => {
    if (
      minDownPaymentPreapproved == null ||
      minRecurringPreapproved == null ||
      minTermPreapproved == null
    ) {
      return;
    }
    createProgramAndNavigate({
      isPaidInFull: false,
      downPmtAmt: minDownPaymentPreapproved,
      isHighRisk: false,
      terms: minTermPreapproved,
      recurringAmt: minRecurringPreapproved,
    });
  }, [
    minDownPaymentPreapproved,
    minRecurringPreapproved,
    minTermPreapproved,
    createProgramAndNavigate,
  ]);

  const addition: CustomProgramContextAddition = {
    customDownPayment,
    setCustomDownPayment,
    customTerm,
    setCustomTerm,
    customRecurringAmount,
    setCustomRecurringAmount,
    downPaymentError,
    recurringAmountError,
    isHighRisk,
    meetsPreauthMinimums,
    minDownPaymentPreapproved,
    minRecurringPreapproved,
    minRecurringPreapprovedForPrompt,
    minTermPreapproved,
    customDownPmt: customDownPmtFinal,
    customTermValue,
    customRecurringAmtValue,
    calculatedTerm,
    calculatedTermDisplay,
    customRecurringAmtFinal: customRecurringAmtFinalClamped,
    handleDownPaymentChange,
    handleRecurringAmountChange,
    handleDownPaymentBlur,
    handleRecurringAmountBlur,
    handleSelectCustomProgram,
    handleSelectMinPreapprovedProgram,
    handleProcessHre,
  };
  return addition;
}
